import React, { useCallback, useEffect, useState } from "react";
import { View, Text, TouchableOpacity, Modal, StyleSheet, Platform } from "react-native";
import { useCameraPermissions } from "expo-camera";
import BarcodeScanner from "../BarcodeScanner";
import colors from "../../../constants/colors";
import { isWebCameraBlockedByInsecureOrigin } from "../../../utils/webCameraContext";

/** Web only: Safari often reports camera as undetermined after remount; remember a prior successful grant for this origin. */
const WEB_CAMERA_GRANTED_STORAGE_KEY = "gohaccp_barcode_camera_granted_v1";

function readWebCameraPriorGrant() {
  if (Platform.OS !== "web" || typeof localStorage === "undefined") {
    return false;
  }
  try {
    return localStorage.getItem(WEB_CAMERA_GRANTED_STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

function persistWebCameraPriorGrant() {
  if (Platform.OS !== "web" || typeof localStorage === "undefined") {
    return;
  }
  try {
    localStorage.setItem(WEB_CAMERA_GRANTED_STORAGE_KEY, "1");
  } catch {
    /* ignore quota / private mode */
  }
}

/**
 * Mobile browsers only grant camera from a direct user gesture (tap).
 * On web, camera also requires a secure context (HTTPS or localhost), or the API stays "denied" with no native prompt.
 */
const BarcodeScannerComponent = ({ closeScanner, onBarCodeScanned }) => {
  const [permission, requestPermission, getPermission] = useCameraPermissions();
  const [blockedHint, setBlockedHint] = useState(false);
  const [webPriorGrant, setWebPriorGrant] = useState(readWebCameraPriorGrant);
  const insecureWeb = Platform.OS === "web" && isWebCameraBlockedByInsecureOrigin();

  useEffect(() => {
    if (typeof getPermission === "function") {
      getPermission().catch(() => {});
    }
  }, [getPermission]);

  const onAllowPress = useCallback(async () => {
    try {
      const result = await requestPermission();
      if (result?.granted) {
        persistWebCameraPriorGrant();
        setWebPriorGrant(true);
      } else if (result && result.canAskAgain === false) {
        setBlockedHint(true);
      }
    } catch (e) {
      console.warn("Camera permission request failed", e);
      setBlockedHint(true);
    }
  }, [requestPermission]);

  const effectiveGranted = !!permission?.granted || webPriorGrant;

  if (insecureWeb) {
    return (
      <Modal transparent visible animationType="slide">
        <View style={styles.backdrop}>
          <View style={styles.card}>
            <Text style={styles.title}>HTTPS required for camera</Text>
            <Text style={styles.body}>
              This page is opened over HTTP (for example http://192.168.1.4). Safari and other browsers treat that as
              insecure: the camera API returns "denied" and the system never shows the allow/deny dialog.
            </Text>
            <Text style={styles.body}>
              To scan on a phone you need a secure origin, for example: https://your-host (TLS certificate), Expo
              tunnel / EAS with HTTPS, or http://localhost when testing on the same machine.
            </Text>
            <TouchableOpacity style={styles.secondaryBtn} onPress={closeScanner}>
              <Text style={styles.secondaryBtnText}>Close</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>
    );
  }

  if (!effectiveGranted && permission == null) {
    return (
      <Modal transparent visible animationType="fade">
        <View style={styles.backdrop}>
          <Text style={styles.loadingText}>Preparing camera…</Text>
          <TouchableOpacity style={styles.cancelBtn} onPress={closeScanner}>
            <Text style={styles.cancelBtnText}>Cancel</Text>
          </TouchableOpacity>
        </View>
      </Modal>
    );
  }

  if (effectiveGranted) {
    return (
      <BarcodeScanner
        onBarCodeScanned={onBarCodeScanned}
        closeScanner={closeScanner}
        unmounCamera={closeScanner}
      />
    );
  }

  return (
    <Modal transparent visible animationType="slide">
      <View style={styles.backdrop}>
        <View style={styles.card}>
          <Text style={styles.title}>Camera access</Text>
          <Text style={styles.body}>
            {Platform.OS === "web"
              ? "Tap “Allow camera” so your browser can use the camera. You must use HTTPS (or localhost); otherwise the browser will not show a permission dialog."
              : "Tap “Allow camera” to continue."}
          </Text>
          <TouchableOpacity style={styles.primaryBtn} onPress={onAllowPress} activeOpacity={0.85}>
            <Text style={styles.primaryBtnText}>Allow camera</Text>
          </TouchableOpacity>
          {blockedHint ? (
            <Text style={styles.hint}>
              Permission was not granted. If you already denied it, open the browser site settings for this page,
              enable Camera, and reload. On iOS Safari, also confirm you are not in Private mode with extra restrictions.
            </Text>
          ) : null}
          <TouchableOpacity style={styles.secondaryBtn} onPress={closeScanner}>
            <Text style={styles.secondaryBtnText}>Cancel</Text>
          </TouchableOpacity>
        </View>
      </View>
    </Modal>
  );
};

export default BarcodeScannerComponent;

const styles = StyleSheet.create({
  backdrop: {
    flex: 1,
    backgroundColor: "rgba(0,0,0,0.55)",
    justifyContent: "center",
    alignItems: "center",
    padding: 24,
  },
  card: {
    backgroundColor: "#fff",
    borderRadius: 12,
    padding: 20,
    maxWidth: 400,
    width: "100%",
  },
  title: {
    fontSize: 20,
    fontWeight: "700",
    marginBottom: 12,
    color: "#111",
  },
  body: {
    fontSize: 15,
    lineHeight: 22,
    color: "#444",
    marginBottom: 16,
  },
  primaryBtn: {
    backgroundColor: colors.primary,
    paddingVertical: 14,
    borderRadius: 8,
    alignItems: "center",
  },
  primaryBtnText: {
    color: "#fff",
    fontSize: 17,
    fontWeight: "600",
  },
  secondaryBtn: {
    marginTop: 12,
    paddingVertical: 12,
    alignItems: "center",
  },
  secondaryBtnText: {
    color: colors.primary,
    fontSize: 16,
  },
  hint: {
    marginTop: 16,
    fontSize: 13,
    lineHeight: 19,
    color: "#a00",
  },
  loadingText: {
    color: "#fff",
    fontSize: 16,
    marginBottom: 16,
  },
  cancelBtn: {
    paddingVertical: 10,
    paddingHorizontal: 20,
  },
  cancelBtnText: {
    color: "#fff",
    fontSize: 16,
    textDecorationLine: "underline",
  },
});
